import { debounce } from 'es-toolkit';

import GameState from '~/modules/game-engine/game-state/game-state';
import InputManager from '~/modules/game-engine/input/input-manager';
import events from '~/modules/game-events/game-events';
import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import storage from '~/modules/utils/storage';
import inputSourceListManager from '~/routes/select-input/input-sources/index';
import { InputSourceNames } from '~/routes/select-input/input-sources/interfaces';

const SELECTED_INPUTS_KEY = 'playerselectedinputs';

export interface SelectedPlayerInput {
  source: InputSourceNames;
  deviceId?: string;
  channel: number;
}

export class PlayerEntity {
  public nameOverride: string = '';
  constructor(
    public number: PlayerNumber,
    public input: SelectedPlayerInput,
    public name?: string,
  ) {}

  public setNumber = (newNumber: PlayerNumber) => {
    const oldNumber = this.number;
    if (oldNumber === newNumber) return;
    this.number = newNumber;
    events.playerNumberChanged.dispatch(newNumber, oldNumber);
  };

  public clone = (): PlayerEntity => {
    const cloned = new PlayerEntity(this.number, { ...this.input }, this.name);
    cloned.nameOverride = this.nameOverride;
    return cloned;
  };

  public changeInput = (input: InputSourceNames, channel = 0, deviceId?: string) => {
    let restartMonitoringPromise: null | Promise<void> = null;
    if (InputManager.monitoringStarted()) {
      restartMonitoringPromise = InputManager.stopMonitoring();
    }

    const newInput: SelectedPlayerInput = { source: input, deviceId, channel };
    const oldInput = this.input;
    this.input = newInput;

    events.playerInputChanged.dispatch(this.number, oldInput, newInput);

    if (restartMonitoringPromise) {
      restartMonitoringPromise.then(InputManager.startMonitoring);
    }
  };

  public toJSON = () => ({
    number: this.number,
    input: this.input,
    name: this.name,
  });

  // "Player 4", not "Player #4": the frames name an unclaimed slot that way and so does every other
  // place that writes a player number out, so the fallback matches rather than being the one outlier.
  public getName = () => {
    return this.nameOverride || (this.name ?? `Player ${this.number + 1}`);
  };

  public isDefaultName = () => this.name === undefined;

  /** `undefined` clears the name, so `getName` falls back to "Player N" - see `isDefaultName`. */
  public setName = (newName: string | undefined) => {
    const oldName = this.name;
    this.name = newName;
    events.playerNameChanged.dispatch(this.number, oldName);
  };

  public static fromJSON = (data: ReturnType<PlayerEntity['toJSON']>) =>
    new PlayerEntity(data.number, data.input, data.name);
}

export interface PlayersSnapshot {
  players: PlayerEntity[];
  minPlayerNumber: number;
}

class PlayersManager {
  private minPlayerNumber = 2;

  private players: PlayerEntity[] = [];
  private requestingPromise: Promise<boolean> | null = null;
  public constructor() {
    const storedPlayers = storage.session.getItem<ReturnType<PlayerEntity['toJSON']>[]>(SELECTED_INPUTS_KEY) ?? [];
    if (storedPlayers.length) {
      this.players = storedPlayers.map(PlayerEntity.fromJSON);

      if (this.getPlayers().some((player) => player.input.source === 'Microphone')) {
        // If any microphones are selected, load the list
        inputSourceListManager.loadMics();
      }
      if (this.getPlayers().some((player) => player.input.source === 'Remote Microphone')) {
        // If there are some remote mics, restart the server
        RemoteMicServer.start();
      }
    } else {
      this.players = [
        new PlayerEntity(0, { source: 'Dummy', deviceId: 'default', channel: 0 }),
        new PlayerEntity(1, { source: 'Dummy', deviceId: 'default', channel: 1 }),
      ];
    }

    events.playerInputChanged.subscribe((playerNumber, _, newInput) => {
      const player = this.getPlayer(playerNumber);
      if (!newInput || !player) return;

      if (newInput.source === 'Remote Microphone') {
        const microphone = RemoteMicManager.getRemoteMicById(newInput.deviceId!);

        if (microphone) {
          player.nameOverride = microphone.name;
        }
      } else {
        player.nameOverride = '';
      }

      this.storePlayers();
    });

    events.playerNameChanged.subscribe(debounce(this.storePlayers, 1000));

    events.remoteMicConnected.subscribe(({ id, name }) => {
      this.getPlayers().forEach((player) => {
        if (player.input.source === 'Remote Microphone' && player.input.deviceId === id && player.getName() !== name) {
          player.nameOverride = name;
        }
      });
    });

    events.remoteMicRenamed.subscribe(({ id, name }) => {
      this.getPlayers().forEach((player) => {
        if (player.input.source === 'Remote Microphone' && player.input.deviceId === id) {
          player.nameOverride = name;
          // The input-selection screens re-render off playerInputChanged, not playerNameChanged —
          // dispatch it (with no actual input change) so a rename well after assignment is picked up
          events.playerInputChanged.dispatch(player.number, player.input, player.input);
        }
      });
    });
  }

  private storePlayers = () => {
    storage.session.setItem(
      SELECTED_INPUTS_KEY,
      this.getPlayers().map((player) => player.toJSON()),
    );
  };

  public requestReadiness = async (secondsLeft?: number) => {
    if (!this.requestingPromise) {
      this.requestingPromise = new Promise((resolve) => {
        const request = async () => {
          // todo fix this -- if there's an input that is not connected, no input will be able to
          // confirm readiness. It should request what it can and watch for input list changes
          // and could also be simplified to only care about remote mic.
          // Found when for some reason regular mic was assigned to a player without an id. getInputForPlayerSelected
          // returned null and the readiness was never requested for remote mic connected for the other player.
          const allInputsConnected = !this.getPlayers()
            .filter((player) => player.input.source === 'Remote Microphone')
            .some((player) => inputSourceListManager.getInputForPlayerSelected(player.input, false) === null);

          if (allInputsConnected) {
            await Promise.all(
              this.getPlayers().map((player) =>
                InputManager.sourceNameToInput(player.input.source)
                  .requestReadiness(player.input.deviceId, secondsLeft)
                  .then(() => {
                    events.readinessConfirmed.dispatch(player.input.deviceId!);
                  }),
              ),
            );
            events.inputListChanged.unsubscribe(request);
            this.requestingPromise = null;
            resolve(true);
          }
        };

        events.inputListChanged.subscribe(request);
        request();
      });
    }

    return this.requestingPromise;
  };

  public getPlayers = () => this.players;
  public getPlayer = (number: number) => this.players.find((player) => player.number === number);

  public getMinPlayerNumber = () => this.minPlayerNumber;
  public setMinPlayerNumber = (minPlayerNumber: number) => {
    const previousMinPlayerNumber = this.minPlayerNumber;
    this.minPlayerNumber = minPlayerNumber;
    events.minPlayerNumberChanged.dispatch(previousMinPlayerNumber, minPlayerNumber);
  };

  public addPlayer = (playerNumber: PlayerNumber) => {
    if (GameState.isPlaying() || this.getPlayer(playerNumber)) {
      return;
    }
    const newPlayer = new PlayerEntity(playerNumber, { source: 'Dummy', deviceId: 'default', channel: 0 });
    this.players.push(newPlayer);
    this.storePlayers();

    events.playerAdded.dispatch(newPlayer.number);
    events.playerInputChanged.dispatch(newPlayer.number, undefined, newPlayer.input);

    return newPlayer;
  };

  public removePlayer = (playerNumber: PlayerNumber) => {
    if (GameState.isPlaying()) {
      return;
    }
    const player = this.getPlayer(playerNumber);

    if (player) {
      this.players = this.players.filter((player) => player.number !== playerNumber);
      if (this.players.length < this.minPlayerNumber) {
        this.addPlayer(
          LOCAL_PLAYER_NUMBERS.find((number) => !this.players.find((player) => player.number === number))!,
        );
      }
      this.storePlayers();
      events.playerRemoved.dispatch(player);
    }
  };

  /**
   * Drops every slot still sitting on `Dummy` - the source a player is created with and the one an
   * emptied slot is pointed back at, so it means "nobody has claimed this". A slot left on it is a
   * player the game scores: `DummyInput` reports a swept pitch at full volume, so it hits every
   * note, beats every human in the room by roughly six to one, wears the rank tag and takes the
   * local high score.
   *
   * A room that has set nothing up is left alone - with no real source anywhere there is nobody to
   * be beaten, and the Dummy roster is what lets the game be played without a microphone at all.
   * Restored as a whole rather than removed one by one: `removePlayer` tops the roster back up to
   * the minimum, which is exactly the slot being dropped.
   *
   * Returns the numbers still on the roster, so a caller holding a setup built from it can drop the
   * same players from that.
   */
  /**
   * The players a round started now would have: a slot left on the placeholder `Dummy` input is
   * dropped as the round starts, unless nobody else is left, and so is a seat held for a phone that
   * has been gone for minutes - it would sing every song on 0. The song card reads this too.
   */
  public getSingingPlayers = () => {
    const players = this.getPlayers();
    const singing = players.filter(
      (player) =>
        player.input.source !== 'Dummy' &&
        !(player.input.source === 'Remote Microphone' && RemoteMicManager.isLongGone(player.input.deviceId)),
    );

    return singing.length > 0 ? singing : players;
  };

  public dropUnassignedPlayers = (): PlayerNumber[] => {
    const players = this.getPlayers();
    const singing = this.getSingingPlayers();

    if (singing.length < players.length) {
      this.restore({
        players: singing.map((player) => player.clone()),
        minPlayerNumber: singing.length,
      });
    }

    return this.getPlayers().map((player) => player.number);
  };

  /** Captures the full roster (including each player's input, name, and nameOverride) plus the
   * current minimum-player setting, so it can be restored later with {@link restore}. Players are
   * deep-cloned so later mutations to the live roster don't leak into the snapshot. */
  public snapshot = (): PlayersSnapshot => ({
    players: this.players.map((player) => player.clone()),
    minPlayerNumber: this.minPlayerNumber,
  });

  /** Atomically replaces the roster and minimum-player setting with a previously captured
   * {@link snapshot}. Unlike calling removePlayer/addPlayer to reach the same roster, this never
   * triggers removePlayer's auto-add-to-minimum side effect. */
  public restore = (snapshot: PlayersSnapshot) => {
    const outgoingPlayers = this.players;
    const previousMinPlayerNumber = this.minPlayerNumber;

    this.players = snapshot.players;
    this.minPlayerNumber = snapshot.minPlayerNumber;
    this.storePlayers();

    if (previousMinPlayerNumber !== snapshot.minPlayerNumber) {
      events.minPlayerNumberChanged.dispatch(previousMinPlayerNumber, snapshot.minPlayerNumber);
    }
    outgoingPlayers.forEach((player) => events.playerRemoved.dispatch(player));
    this.players.forEach((player) => {
      events.playerAdded.dispatch(player.number);
      events.playerInputChanged.dispatch(player.number, undefined, player.input);
    });
  };

  public getInputs = () => this.getPlayers().map((player) => player.input);
}

export default new PlayersManager();
